/**
 * @fileoverview Maintenance page for issuing maintenance certificates
 */
import { useState, useEffect } from 'react';
import { CertificateForm } from '@/components/certificates/CertificateForm';
import { CertificatePreview } from '@/components/certificates/CertificatePreview';
import { Modal } from '@/components/common/Modal';
import { useCertificateForm } from '@/hooks/useCertificateForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'react-hot-toast';

export const MaintenancePage = () => {
    const { t } = useLanguage();
    const { token } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [issuanceMethod, setIssuanceMethod] = useState('pdf');
    const certificateForm = useCertificateForm();

    // Set mode to mro on mount
    useEffect(() => {
        if (certificateForm.mode !== 'mro') {
            certificateForm.setMode('mro');
        }
    }, []);

    const handleSubmit = async (data) => {
        setIsSubmitting(true);

        try {
            console.log('[MaintenancePage] Submitting certificate:', data);
            toast.loading(t('cert_form.processing', 'Procesando certificado...'), { id: 'cert-submit' });

            // Prepare request body based on issuance method
            let requestBody;

            if ('pdfFile' in data) {
                // PDF upload method
                toast.dismiss('cert-submit');
                toast.error('La emisión mediante carga de PDF aún no está completamente implementada. Por favor, use el formulario completo.');
                return;
            } else {
                // Form-based issuance
                requestBody = {
                    fieldValues: data,
                    templateId: null,
                    mode: 'mro'
                };
            }

            // Call backend API
            const response = await fetch('/api/certificates', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            toast.dismiss('cert-submit');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('[MaintenancePage] Certificate created:', result);

            toast.success(t('cert_form.cert_issued', '¡Certificado Emitido!'));
            setShowSuccessModal(true);
            certificateForm.reset();

        } catch (error) {
            console.error('[MaintenancePage] Error:', error);
            toast.dismiss('cert-submit');
            toast.error(t('cert_form.issue_error', 'Error al emitir el certificado') + ': ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (<>
        <div className={issuanceMethod === 'pdf' ? 'single-column-layout' : 'form-with-preview-layout'}>
            <CertificateForm
                formData={certificateForm.formData}
                mode="mro"
                setMode={() => { }}
                updateField={certificateForm.updateField}
                errors={certificateForm.errors}
                onSubmit={(data) => {
                    if ('pdfFile' in data) {
                        handleSubmit(data);
                    }
                    else if (certificateForm.validate()) {
                        handleSubmit(data);
                    }
                }}
                isLoading={isSubmitting}
                onIssuanceMethodChange={setIssuanceMethod}
            />
            {issuanceMethod === 'form' && (<CertificatePreview formData={certificateForm.formData} mode="mro" />)}
        </div>

        <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
            <div className="success-icon">
                <i className="fas fa-check-circle"></i>
            </div>
            <h3>{t('cert_form.cert_issued', '¡Certificado Emitido!')}</h3>
            <p style={{ color: '#94a3b8', margin: '15px 0' }}>
                {t('cert_form.cert_issued_desc', 'El documento ha sido firmado digitalmente y enviado al destinatario con éxito.')}
            </p>
            <button className="btn-submit" onClick={() => setShowSuccessModal(false)}>
                {t('common.close', 'Cerrar')}
            </button>
        </Modal>
    </>);
};
